const QTY_EPS = 0.001;
const PRICE_EPS = 0.0001;

function normalizeProjectId(value) {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeHasIgv(value) {
  return value !== false && value !== 'false';
}

function itemLabel(poItem, docItem) {
  return poItem.product?.name || docItem.description || poItem.product_id || docItem.product_id;
}

function docLinesForOrder(documentItems, orderId) {
  const oid = Number(orderId);
  return (documentItems || []).filter((item) => {
    if (item.item_type !== 'product') return false;
    if (item.purchase_order_id && Number(item.purchase_order_id) === oid) return true;
    if (item.purchase_order_item_id) return true;
    return false;
  }).filter((item) => {
    if (!item.purchase_order_item_id) {
      return item.purchase_order_id && Number(item.purchase_order_id) === oid;
    }
    return true;
  });
}

function linesLinkedToOrder(documentItems, order) {
  const poItemIds = new Set((order.items || []).map((i) => Number(i.id)));
  return (documentItems || []).filter((item) => {
    if (item.item_type !== 'product') return false;
    if (item.purchase_order_id && Number(item.purchase_order_id) === Number(order.id)) {
      return true;
    }
    if (item.purchase_order_item_id && poItemIds.has(Number(item.purchase_order_item_id))) {
      return true;
    }
    return false;
  });
}

/**
 * Detecta si el documento difiere de la orden de compra
 * (productos, cantidades, proyecto, precio unitario, IGV o líneas faltantes).
 */
export function analyzePurchaseOrderDocumentMismatch(documentItems, purchaseOrder) {
  if (!purchaseOrder?.id) {
    return { hasMismatch: false, reasons: [] };
  }

  if (purchaseOrder.fulfillment_closed_at || purchaseOrder.is_fulfillment_closed) {
    return { hasMismatch: false, reasons: [], alreadyClosed: true };
  }

  const reasons = [];
  const linked = linesLinkedToOrder(documentItems, purchaseOrder);
  const orderItemIds = new Set((purchaseOrder.items || []).map((i) => Number(i.id)));

  linked.forEach((docItem) => {
    const poItemId = docItem.purchase_order_item_id
      ? Number(docItem.purchase_order_item_id)
      : null;
    if (poItemId && !orderItemIds.has(poItemId)) {
      reasons.push('Hay ítems del documento vinculados a otra orden.');
      return;
    }
    const poItem = poItemId
      ? purchaseOrder.items.find((i) => Number(i.id) === poItemId)
      : purchaseOrder.items.find((i) => Number(i.product_id) === Number(docItem.product_id));
    if (!poItem) {
      if (docItem.purchase_order_id && Number(docItem.purchase_order_id) === Number(purchaseOrder.id)) {
        reasons.push(
          `Producto en documento no está en la orden (${docItem.description || docItem.product_id}).`,
        );
      }
      return;
    }
    if (Number(docItem.product_id) !== Number(poItem.product_id)) {
      const name = poItem.product?.name || poItem.product_id;
      reasons.push(`Producto distinto al de la orden para «${name}».`);
    }
    const docQty = parseFloat(docItem.quantity) || 0;
    const pending = parseFloat(poItem.pending_quantity ?? poItem.quantity) || 0;
    if (poItemId && Math.abs(docQty - pending) > QTY_EPS) {
      reasons.push(
        `Cantidad distinta para «${itemLabel(poItem, docItem)}» (doc: ${docQty}, pendiente OC: ${pending}).`,
      );
    }

    if (poItemId) {
      const name = itemLabel(poItem, docItem);
      const docProject = normalizeProjectId(docItem.project_id);
      const poProject = normalizeProjectId(poItem.project_id);
      if (docProject !== poProject) {
        reasons.push(`Proyecto distinto para «${name}».`);
      }

      const docPrice = parseFloat(docItem.unit_price) || 0;
      const poPrice = parseFloat(poItem.unit_price) || 0;
      if (Math.abs(docPrice - poPrice) > PRICE_EPS) {
        reasons.push(
          `Precio unitario distinto para «${name}» (doc: ${docPrice}, OC: ${poPrice}).`,
        );
      }

      const docHasIgv = normalizeHasIgv(docItem.has_igv);
      const poHasIgv = normalizeHasIgv(poItem.has_igv);
      if (docHasIgv !== poHasIgv) {
        reasons.push(
          `IGV distinto para «${name}» (doc: ${docHasIgv ? 'Sí' : 'No'}, OC: ${poHasIgv ? 'Sí' : 'No'}).`,
        );
      }
    }
  });

  (purchaseOrder.items || []).forEach((poItem) => {
    const pending = parseFloat(poItem.pending_quantity ?? poItem.quantity) || 0;
    if (pending <= QTY_EPS) return;
    const docForLine = linked.filter(
      (d) => Number(d.purchase_order_item_id) === Number(poItem.id),
    );
    if (docForLine.length === 0) {
      reasons.push(
        `Falta en el documento: «${poItem.product?.name || poItem.product_id}» (pendiente ${pending}).`,
      );
    }
  });

  const hasOrphanOnOrder =
    linked.some((d) => !d.purchase_order_item_id) ||
    docLinesForOrder(documentItems, purchaseOrder.id).some(
      (d) =>
        d.purchase_order_id &&
        Number(d.purchase_order_id) === Number(purchaseOrder.id) &&
        !d.purchase_order_item_id,
    );

  if (hasOrphanOnOrder && linked.length > 0) {
    const already = reasons.some((r) => r.includes('no está en la orden'));
    if (!already) {
      reasons.push('Hay productos en el documento sin línea exacta de la orden de compra.');
    }
  }

  const unique = [...new Set(reasons)];

  return {
    hasMismatch: unique.length > 0,
    reasons: unique,
    alreadyClosed: false,
  };
}

/**
 * Órdenes de compra seleccionadas o referenciadas en ítems con diferencias respecto al documento.
 */
export function getPurchaseOrdersWithDocumentMismatch(
  documentItems,
  selectedOrderIds,
  pendingPurchaseOrders,
) {
  const orderMap = new Map();
  (pendingPurchaseOrders || []).forEach((o) => orderMap.set(Number(o.id), o));

  const ids = new Set();
  (selectedOrderIds || []).forEach((id) => ids.add(Number(id)));
  (documentItems || []).forEach((item) => {
    if (item.purchase_order_id) ids.add(Number(item.purchase_order_id));
  });

  return [...ids]
    .map((id) => {
      const order = orderMap.get(id);
      if (!order) return null;
      const analysis = analyzePurchaseOrderDocumentMismatch(documentItems, order);
      if (!analysis.hasMismatch) return null;
      return { order, ...analysis };
    })
    .filter(Boolean);
}
